import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Row } from 'reactstrap';
import styled from 'styled-components';
import dimension from '../../../../res/styles/Responsive';
import themeButton from '../../../../res/styles/theme_button.module.scss';
import { calculateFinalHa } from '../../../../service/CommonFunctions';
import { extractExtraReportCharacters } from '../../../../service/TechFormDetailService';
import Checkbox from './../../../../components/Checkbox';
import { ColNoPadding, HorizontalDiv } from './../../../../components/Common';
import ConfirmDialog from './../../../../components/ConfirmDialog';
import InfoText from './../../../../components/InfoText';
import { ExtraReportCharacterNames } from './../../../../res/Constants';
import CrAlertPrinter from './CrAlertPrinter';
import EditableInfoText from './EditableInfoText';

CrAlertItemDetail.propTypes = {
    className: PropTypes.string,
    isEditable: PropTypes.bool,
    onToggleEdit: PropTypes.func,
    onEditingCompleted: PropTypes.func,
    onFieldEdited: PropTypes.func,
    onDelete: PropTypes.func,
    data: PropTypes.object
};

CrAlertItemDetail.defaultProps = {
    className: '',
    data: {},
    onDelete: () => { }
};

export default function CrAlertItemDetail(props) {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const {
        netHa,
        destroyedHa,
        destroyedReason,
        informedCustomer,
        indemnity,
        notes
    } = extractExtraReportCharacters(props.data.extraReportCharacters);
    const finalHa = calculateFinalHa(netHa.value, destroyedHa.value).toString();

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = (crAlertPk, confirmed) => {
        setShowDeleteConfirm(false);
        if (confirmed) {
            props.onDelete(crAlertPk);
        }
    };

    return (
        <StyledCrAlertContainer className={props.className}>
            <Row>
                <ColNoPadding>
                    <SmallEditableInfoText
                        type='number'
                        title='Net Ha'
                        initialValue={netHa.value}
                        isEditable={props.isEditable}
                        fieldId={ExtraReportCharacterNames.NET_HA}
                        onFieldEdited={props.onFieldEdited}
                    />
                </ColNoPadding>
                <ColNoPadding>
                    <SmallEditableInfoText
                        type='number'
                        title='Destroyed Ha'
                        initialValue={destroyedHa.value}
                        isEditable={props.isEditable}
                        fieldId={ExtraReportCharacterNames.DESTROYED_HA}
                        onFieldEdited={props.onFieldEdited}
                    />
                </ColNoPadding>
                <ColNoPadding>
                    <InfoText title='Final Ha' content={finalHa} />
                </ColNoPadding>
                <ColNoPadding className='d-none d-md-block'>
                    <DestroyedReasonEditableInfoText
                        isEditable={props.isEditable}
                        destroyedReason={destroyedReason.value}
                        onFieldEdited={props.onFieldEdited}
                    />
                </ColNoPadding>
            </Row>
            <Row className='d-md-none'>
                <DestroyedReasonEditableInfoText
                    isEditable={props.isEditable}
                    destroyedReason={destroyedReason.value}
                    onFieldEdited={props.onFieldEdited}
                />
            </Row>
            <Row>
                <ColNoPadding>
                    <StyledCheckbox
                        text='Customer has been informed?'
                        isHorizontal={false}
                        isSelected={informedCustomer.value === 'true'}
                        customId={ExtraReportCharacterNames.INFORMED_CUSTOMER}
                        isEditable={props.isEditable}
                        onChange={props.onFieldEdited}
                    />
                </ColNoPadding>
                <ColNoPadding>
                    <StyledCheckbox
                        text='Indemnity'
                        isHorizontal={false}
                        isSelected={indemnity.value === 'true'}
                        customId={ExtraReportCharacterNames.INDEMNITY}
                        isEditable={props.isEditable}
                        onChange={props.onFieldEdited}
                    />
                </ColNoPadding>
            </Row>
            {(notes || props.isEditable) && (
                <Row>
                    <ColNoPadding>
                        <EditableInfoText
                            isEditable={props.isEditable}
                            onFieldEdited={props.onFieldEdited}
                            title='Notes'
                            fieldId={ExtraReportCharacterNames.NOTES}
                            initialValue={notes.value}
                            rows={3}
                        />
                    </ColNoPadding>
                </Row>
            )}
            <FooterRow>
                {props.isEditable ? (
                    <>
                        <ColNoPadding>
                            <CancelButton onClick={props.toggleEdit}>
                                CANCEL
                            </CancelButton>
                        </ColNoPadding>
                        <ColNoPadding>
                            <OkButton onClick={props.onEditingCompleted}>
                                OK
                            </OkButton>
                        </ColNoPadding>
                    </>
                ) : (
                    <>
                        <ColNoPadding>
                            <ButtonsContainer>
                                <EditButton onClick={props.toggleEdit}>
                                    EDIT
                                </EditButton>
                                <DeleteButton onClick={handleDeleteClick}>
                                    DELETE
                                </DeleteButton>
                            </ButtonsContainer>
                        </ColNoPadding>
                        <ColNoPadding>
                            {/* Avoid to create a Printable CR-Alert if the Cr-Alert hasn't been saved to the server*/}
                            {props.data.pk != null && (
                                <CrAlertPrinter crAlert={props.data}>
                                    <PrintButton>
                                        <StyledImg
                                            src={require('../../../../res/images/cac_print_black.png')}
                                            alt='print button'
                                        />
                                        PRINT
                                    </PrintButton>
                                </CrAlertPrinter>
                            )}
                        </ColNoPadding>
                    </>
                )}
            </FooterRow>
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                text="Are you sure you want to delete this CR-Alert?"
                elementId={props.data.pk}
                onConfirm={handleDeleteConfirm}
            />
        </StyledCrAlertContainer>
    );
}

const DestroyedReasonEditableInfoText = ({
    destroyedReason,
    onFieldEdited,
    isEditable,
    ...props
}) => {
    return (
        <EditableInfoText
            {...props}
            isEditable={isEditable}
            title='Destruction Explanation'
            fieldId={ExtraReportCharacterNames.DESTROYED_REASON}
            initialValue={destroyedReason}
            onFieldEdited={onFieldEdited}
        />
    );
};

const SmallEditableInfoText = styled(EditableInfoText)`
    &.editable input {
        width: 70px;
    }
`;

const StyledCrAlertContainer = styled.div`
    & > *:not(:last-child) {
        margin-bottom: 10px;
    }
`;

const StyledImg = styled.img`
    width: 20px;
    margin-right: 10px;
`;

const StyledCheckbox = styled(Checkbox).attrs({
    className: 'flex-column'
})`
    margin: inherit;
    font-size: 12px;

    @media ${dimension.md} {
        font-size: 13px;
    }
`;

const FooterRow = styled(Row)`
    justify-content: space-between;

    .col {
        text-align: center;
    }
`;

const ButtonsContainer = styled(HorizontalDiv)`
    gap: 10px;
    margin: auto;
    justify-content: center;
    width: 100%;

    @media ${dimension.md} {
        margin: 0;
        width: auto;
    }
`;

const EditButton = styled.button.attrs({
    className: themeButton.red
})`
    width: 150px;
    min-width: 150px;
    flex-shrink: 0;
    
    @media (max-width: 767px) {
        width: 45%;
        min-width: 120px;
        max-width: 150px;
    }
`;

const DeleteButton = styled.button.attrs({
    className: themeButton.red
})`
    width: 150px;
    min-width: 150px;
    flex-shrink: 0;
    
    @media (max-width: 767px) {
        width: 45%;
        min-width: 120px;
        max-width: 150px;
    }
`;

const CancelButton = styled.button.attrs({
    className: themeButton.red
})`
    width: 80%;

    @media ${dimension.md} {
        width: 150px;
        margin: 0;
    }
`;

const OkButton = styled.button.attrs({
    className: themeButton.green
})`
    width: 80%;

    @media ${dimension.md} {
        width: 150px;
        margin: 0;
    }
`;

const PrintButton = styled.button.attrs({
    className: themeButton.textGreen
})`
    width: 80%;

    @media ${dimension.md} {
        width: 150px;
        margin: 0;
    }
`;
